import { Component, SecurityContext } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeValue } from '@angular/platform-browser';
import { KendoPromptType } from './kendo-prompt.models';

/**
 * A component that renders a prompt dialog with an input field.
 * 
 * This component is used within the Kendo prompt service to display
 * a dialog that allows users to input text or numeric values.
 * It handles both text and number input types and sanitizes HTML content.
 * 
 * @example
 * ```typescript
 * // Used via KendoAlertService
 * const result = await this.kendoAlertService.promptAsync(
 *   'Enter your age:',
 *   'Age Input',
 *   KendoPromptType.Number,
 *   25
 * );
 * if (result !== null) {
 *   console.log('User entered:', result);
 * } else {
 *   console.log('User cancelled');
 * }
 * ```
 */
@Component({
  selector: 'kendo-prompt',
  template: `<div [innerHtml]="html"></div>
    <input class="k-input" [type]="type" [(ngModel)]="value" />`,
  imports: [FormsModule],
})
export class KendoPromptComponent {
  /**
   * The sanitized HTML content to display in the prompt dialog.
   */
  html?: SafeValue;

  /**
   * @internal
   * Private storage for the message text.
   */
  private _msg: string = '';

  /**
   * Gets the current message text.
   * @returns The current message text string.
   */
  get msg(): string {
    return this._msg;
  }

  /**
   * Sets the message text and converts it to sanitized HTML.
   * @param value The message text to display in the prompt dialog.
   */
  set msg(value: string) {
    this._msg = value;
    this.html = this.getHtml(value);
  }

  /**
   * Creates an instance of KendoPromptComponent.
   * @param sanitizer The DomSanitizer service for sanitizing HTML content.
   */
  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Sanitizes the provided HTML string and converts it to a trusted HTML value.
   * @param html The HTML string to sanitize.
   * @returns A SafeValue that can be used with [innerHtml].
   */
  getHtml(html: string): SafeValue {
    let safeHtml = this.sanitizer.sanitize(SecurityContext.STYLE, html)!;
    return this.sanitizer.bypassSecurityTrustHtml(safeHtml);
  }

  /**
   * The type of prompt to display (Number or String).
   * @default KendoPromptType.Number
   */
  PromptType: KendoPromptType = KendoPromptType.Number;

  /**
   * Gets the HTML input type based on the current PromptType.
   * @returns 'number' for KendoPromptType.Number, 'text' for KendoPromptType.String.
   */
  get type(): string {
    return this.PromptType === KendoPromptType.Number ? 'number' : 'text';
  }

  /**
   * The current value of the input field.
   * The type depends on the PromptType (number or string).
   * @default null
   */
  value: number | string | null = null;
}
